import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useRewardTierApplied } from '@fhs/loyalty/src/hooks/use-reward-tier-applied';
import { Button, IconLocked, tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { Network } from '@fhs-legacy/frontend/src/utils/network/status';
import { getDateNowByTimezone, isRestaurantOpen } from '@fhs-legacy/frontend/src/utils/restaurant';
import { CART_PLACE_ORDER_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import { usePaymentMethodStore } from '../../../payment';
import { PaymentOrchestrator } from '../../../payment/core/payment-orchestrator';
import { useBuildPipelineContext } from '../../../payment/hooks/use-build-pipeline-context';
import {
  FIRE_SUCCESSFUL,
  ORDER_SUBMITTED_STATUSES,
  type OrderSubmittedStatus,
} from '../../../payment/order-status';
import type { PaymentPipelineError, PaymentPipelineResult } from '../../../payment/payment-types';
import { useCartStore } from '../../../state/cart-store';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';
import { MAX_TIP_AMOUNT_CENTS, useTipStore } from '../../../state/tip-store';
import { logCartPlaceOrder, logPaymentRetry } from '../../../utils/logging-utils';

import { getPlaceOrderButtonText } from './get-place-order-button-text';
import { handlePlaceOrder } from './handle-place-order';
import { ConfirmingOrderAlert, PaymentErrorAlert, PricingAlert } from './payment-alerts';
import {
  getDefaultRetryStep,
  isCommitFailureRetry,
  isRetryablePaymentError,
} from './payment-retry-utils';

export default function PlaceOrderButton() {
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentError, setCurrentError] = useState<PaymentPipelineError | null>(null);
  const [pendingCommitRetry, setPendingCommitRetry] = useState(false);
  const lastResultRef = useRef<PaymentPipelineResult | null>(null);
  const orchestratorRef = useRef<PaymentOrchestrator>(new PaymentOrchestrator());
  const pricingStartedRef = useRef(false);
  const pressInFlightRef = useRef(false);
  const confirmingStartedRef = useRef(false);

  // Store hooks
  const cartEntries = useCartStore(s => s.cartEntries);
  const isPaymentReady = usePaymentMethodStore(state => state.isPaymentReady());
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const userAccountsLoadError = usePaymentMethodStore(state => state.userAccountsLoadError);
  const setUserAccountsLoadError = usePaymentMethodStore(state => state.setUserAccountsLoadError);
  // Track the previous payment method to detect when user changes it
  const prevPaymentMethodRef = useRef(selectedPaymentMethod);
  const otherTipValue = useTipStore(state => state.otherTipValue);
  const selectedTip = useTipStore(state => state.selectedTip);
  const { buildPipelineContext } = useBuildPipelineContext();
  const {
    setShouldSkipPriceOrder,
    setShouldSkipPriceOrderStatusPolling,
    serverOrderLoading,
    serverOrderError,
    serverOrder,
    tryPricing,
  } = useCartContext();
  const { isDelivery } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { isOfferValidationError } = useLoyaltyContext();
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { rewardTierApplied } = useRewardTierApplied();

  const intl = useIntl();
  const hasReachedMaxRedemptionLimit = useAppSelector(
    selectors.loyalty.createHasReachedMaxRedemptionLimitSelector(intl)
  );

  const currentState = usePaymentFlowStore(state => state.currentState);
  const retryInfo = usePaymentFlowStore(state => state.retryInfo);
  const isCommitRetry = usePaymentFlowStore(state => state.isCommitRetry);
  const isOfflineDuringCommit = usePaymentFlowStore(state => state.isOfflineDuringCommit);
  const enterOfflineConfirming = usePaymentFlowStore(state => state.enterOfflineConfirming);
  const setIsOfflineDuringCommit = usePaymentFlowStore(state => state.setIsOfflineDuringCommit);
  const setRbiOrderId = usePaymentFlowStore(state => state.setRbiOrderId);
  const setCurrentState = usePaymentFlowStore(state => state.setCurrentState);
  const setRetryInfo = usePaymentFlowStore(state => state.setRetryInfo);
  const setIsCommitRetry = usePaymentFlowStore(state => state.setIsCommitRetry);
  const { logRBIEvent } = useCRMEventsContext();

  // All Button States
  const serverOrderProcessing =
    serverOrderLoading ||
    serverOrder?.status === 'PRICE_REQUESTED' ||
    serverOrder?.status === 'INSERT_REQUESTED' ||
    // @ts-ignore   not yet generated in type
    serverOrder?.status === 'FIRE_REQUESTED' ||
    (serverOrderLoading && serverOrder?.status !== 'INSERT_ERROR'); // STOP INFINITE LOADING ON ERROR

  // Pipeline is processing when currentState indicates active processing
  // (e.g., when handlePlaceOrder is called from gift card reload action sheet)
  const isPipelineProcessing =
    currentState !== 'idle' && currentState !== 'success' && currentState !== 'error';

  const hasCurrentPrice = serverOrder?.status === 'PRICE_SUCCESSFUL';
  const hasValidOrderForCommit =
    hasCurrentPrice ||
    serverOrder?.status === 'PAYMENT_ERROR' ||
    serverOrder?.status === 'VALIDATION_ERROR';
  const retryErrorCode = retryInfo?.errorCode ?? currentError?.code;
  const hasRetryableFailure =
    !!retryInfo || isRetryablePaymentError(currentError?.code);
  const needsCommitReprice = isCommitFailureRetry(retryErrorCode);
  const canPlaceOrder = hasValidOrderForCommit || hasRetryableFailure;
  const isDeliveryFeesLoading = isDelivery && !serverOrder?.delivery;
  const isAwaitingUserRetry =
    hasRetryableFailure &&
    (currentState === 'error' || !!currentError) &&
    !pendingCommitRetry &&
    !isProcessing;
  const isButtonLoading =
    !isAwaitingUserRetry &&
    (isProcessing ||
      serverOrderProcessing ||
      isPipelineProcessing ||
      offersLoading ||
      isDeliveryFeesLoading);

  if (currentState === 'confirming' || (currentState === 'committing' && isOfflineDuringCommit)) {
    confirmingStartedRef.current = true;
  } else if (currentError || currentState === 'idle' || currentState === 'success') {
    confirmingStartedRef.current = false;
  }

  const isHandoffFromConfirming =
    confirmingStartedRef.current && !currentError && currentState === 'error';

  const isConfirmingSubmittedOrder =
    !pendingCommitRetry &&
    (!isCommitRetry || isOfflineDuringCommit) &&
    (currentState === 'confirming' ||
      isOfflineDuringCommit ||
      isHandoffFromConfirming ||
      (serverOrder?.status === 'INSERT_REQUESTED' &&
        (currentState === 'error' || !!currentError)));

  const isButtonDisabled =
    isOfferValidationError ||
    !isPaymentReady ||
    (!isAwaitingUserRetry &&
      (isProcessing || serverOrderProcessing || isPipelineProcessing)) ||
    isDeliveryFeesLoading ||
    !canPlaceOrder ||
    !cartEntries.length ||
    (!!serverOrderError && !isConfirmingSubmittedOrder && !hasRetryableFailure) ||
    !!(hasReachedMaxRedemptionLimit && rewardTierApplied) ||
    (selectedTip === 'other' && Number(otherTipValue) > MAX_TIP_AMOUNT_CENTS);

  const buttonFlowState =
    (isCommitRetry || pendingCommitRetry) && currentState === 'confirming'
      ? 'committing'
      : currentState === 'committing' && isOfflineDuringCommit && !isCommitRetry && !pendingCommitRetry
        ? 'confirming'
        : currentState;

  const buttonText = getPlaceOrderButtonText(
    buttonFlowState,
    hasRetryableFailure,
    serverOrderLoading || isDeliveryFeesLoading,
    serverOrder?.status
  );

  // Clear payment error when user changes payment method
  // When Google Pay (or Apple Pay) fails, the error shows and payment method stays the same.
  // When user selects a different payment method, the error clears.
  useEffect(() => {
    if (selectedPaymentMethod !== prevPaymentMethodRef.current) {
      // Payment method changed - clear any existing error
      if (currentError) {
        setCurrentError(null);
        lastResultRef.current = null;
      }
      if (userAccountsLoadError) {
        setUserAccountsLoadError(null);
      }
      prevPaymentMethodRef.current = selectedPaymentMethod;
    }
  }, [selectedPaymentMethod, currentError, userAccountsLoadError, setUserAccountsLoadError]);

  useEffect(() => {
    if (
      currentError &&
      isRetryablePaymentError(currentError.code) &&
      !retryInfo
    ) {
      const retryFromStep = getDefaultRetryStep(currentError.code);
      if (retryFromStep) {
        setRetryInfo({
          canRetry: true,
          retryFromStep,
          errorCode: currentError.code,
          errorMessage: currentError.message,
        });
      }
    }
  }, [currentError, retryInfo, setRetryInfo]);

  useEffect(() => {
    if (
      currentState === 'error' &&
      hasRetryableFailure &&
      !pendingCommitRetry &&
      isProcessing
    ) {
      setIsProcessing(false);
      pressInFlightRef.current = false;
    }
  }, [currentState, hasRetryableFailure, pendingCommitRetry, isProcessing]);

  useEffect(() => {
    let lastConnected: boolean | null = null;

    const listener = Network.addListener(status => {
      if (lastConnected === null) {
        lastConnected = status.connected;
        return;
      }
      if (lastConnected === status.connected) {
        return;
      }
      lastConnected = status.connected;

      if (!status.connected) {
        const { currentState: flowState } = usePaymentFlowStore.getState();
        const pipelineActive =
          flowState !== 'idle' && flowState !== 'success' && flowState !== 'error';

        if (isProcessing || pressInFlightRef.current || pipelineActive) {
          enterOfflineConfirming();
        }
        return;
      }

      setIsOfflineDuringCommit(false);
      setShouldSkipPriceOrderStatusPolling(false);

      const { currentState: flowState } = usePaymentFlowStore.getState();
      const awaitingOrderStatus =
        flowState === 'confirming' ||
        flowState === 'insertRequested' ||
        currentError?.code === 'COMMIT_ORDER_UNCONFIRMED';

      if (awaitingOrderStatus) {
        setCurrentState('confirming');
        setIsProcessing(true);
        pressInFlightRef.current = true;
        return;
      }

      if (hasRetryableFailure || currentError) {
        setIsProcessing(false);
        pressInFlightRef.current = false;
      }
    });
    return () => listener.remove();
  }, [
    currentError,
    enterOfflineConfirming,
    hasRetryableFailure,
    isProcessing,
    setCurrentState,
    setIsOfflineDuringCommit,
    setShouldSkipPriceOrderStatusPolling,
  ]);

  const placeOrder = useCallback(async () => {
    try {
      await handlePlaceOrder({
        lastResultRef,
        orchestratorRef,
        buildPipelineContext,
        setIsProcessing,
        setShouldSkipPriceOrder,
        setCurrentError,
        logRBIEvent,
      });
    } finally {
      pressInFlightRef.current = false;
    }
  }, [buildPipelineContext, setShouldSkipPriceOrder, logRBIEvent]);

  // After a COMMIT_ORDER_FAILED retry, wait for repricing to complete before retrying the pipeline.
  // Uses pricingStartedRef to avoid firing immediately when serverOrder.status is already
  // PRICE_SUCCESSFUL from the previous (pre-failure) pricing cycle.
  useEffect(() => {
    if (!pendingCommitRetry) {
      pricingStartedRef.current = false;
      return;
    }

    if (serverOrderLoading || serverOrder?.status === 'PRICE_REQUESTED') {
      pricingStartedRef.current = true;
      return;
    }

    if (!pricingStartedRef.current) {
      const timeout = setTimeout(() => {
        if (!pricingStartedRef.current) {
          setPendingCommitRetry(false);
          setIsCommitRetry(false);
          setIsProcessing(false);
          pressInFlightRef.current = false;
        }
      }, 1000);

      return () => clearTimeout(timeout);
    }

    setPendingCommitRetry(false);

    if (serverOrder?.status === 'PRICE_SUCCESSFUL') {
      void placeOrder();
    } else {
      setIsCommitRetry(false);
      setIsProcessing(false);
      pressInFlightRef.current = false;
    }

    return undefined;
  }, [
    pendingCommitRetry,
    serverOrderLoading,
    serverOrder?.status,
    placeOrder,
    setIsCommitRetry,
  ]);

  useEffect(() => {
    const status = serverOrder?.status as OrderSubmittedStatus | undefined;
    const inRecovery =
      currentState === 'insertRequested' ||
      currentState === 'error' ||
      currentState === 'confirming' ||
      isProcessing;

    if (status && inRecovery && ORDER_SUBMITTED_STATUSES.has(status)) {
      // Stay on checkout until network is back and GetOrder shows a terminal success.
      if (
        !isOfflineDuringCommit &&
        (status === 'INSERT_SUCCESSFUL' || status === FIRE_SUCCESSFUL)
      ) {
        setCurrentError(null);
        setRetryInfo(null);
        setCurrentState('success');
        setIsProcessing(false);
        pressInFlightRef.current = false;
        return;
      }

      if (currentState === 'confirming' || currentState === 'error') {
        setCurrentError(null);
        setRetryInfo(null);
        setCurrentState('insertRequested');
      }
      return;
    }

    if (currentState === 'confirming') {
      return;
    }

    if (currentState === 'insertRequested' && serverOrder?.status === 'INSERT_ERROR') {
      setIsProcessing(false);
      setCurrentState('error');
    }
  }, [
    currentState,
    isOfflineDuringCommit,
    serverOrder?.status,
    isProcessing,
    retryInfo,
    setCurrentState,
    setRetryInfo,
  ]);

  const handleClearError = () => {
    setCurrentError(null);
    lastResultRef.current = null;
  };

  const handleClearUserAccountsLoadError = () => {
    setUserAccountsLoadError(null);
  };

  const userAccountsLoadPipelineError: PaymentPipelineError | null = userAccountsLoadError
    ? {
        code: 'USER_ACCOUNTS_LOAD_FAILED',
        message: userAccountsLoadError,
        userMessage: userAccountsLoadError,
      }
    : null;

  const handlePress = async () => {
    if (pressInFlightRef.current) {
      return;
    }
    pressInFlightRef.current = true;
    setIsProcessing(true);
    setCurrentError(null);
    lastResultRef.current = null;
    setIsOfflineDuringCommit(false);

    if (isDelivery && store?.number) {

      const fetchedStore = await fetchStore(store.number);
      if (
        fetchedStore &&
        !isRestaurantOpen(
          fetchedStore.deliveryHours,
          getDateNowByTimezone({ timeZone: fetchedStore.timezone || undefined })
        )
      ) {
        setIsProcessing(false);
        pressInFlightRef.current = false;
        return;
      }
    }

    if (hasRetryableFailure) {
      logPaymentRetry(
        logRBIEvent,
        retryInfo
          ? `after failed ${retryInfo.failedStep}. Retrying from ${retryInfo.retryFromStep}`
          : `after payment error ${retryErrorCode}. Retrying checkout`
      );

      if (needsCommitReprice) {
        if (serverOrder?.rbiOrderId) {
          setRbiOrderId(serverOrder.rbiOrderId);
        }
        setIsCommitRetry(true);
        setIsProcessing(true);
        setShouldSkipPriceOrderStatusPolling(false);
        tryPricing();
        setPendingCommitRetry(true);
        return;
      }

      setIsCommitRetry(true);
    } else {
      logCartPlaceOrder(logRBIEvent);
    }

    // set rbiOrderId for routing off cart or checkout page
    if (serverOrder?.rbiOrderId) {
      setRbiOrderId(serverOrder.rbiOrderId);
    }

    void placeOrder();
  };

  // Hold pricing/payment errors while INSERT_REQUESTED — the poll is still confirming the order.
  const showPricingAlert = !!serverOrderError && !isPipelineProcessing && !isConfirmingSubmittedOrder;
  const showPaymentError = !!currentError && !isConfirmingSubmittedOrder;

  return (
    <View style={[styles.container, styles.buttonContainer]}>
      {isConfirmingSubmittedOrder && <ConfirmingOrderAlert />}
      {showPricingAlert && <PricingAlert />}
      {userAccountsLoadPipelineError && (
        <PaymentErrorAlert
          error={userAccountsLoadPipelineError}
          onClear={handleClearUserAccountsLoadError}
        />
      )}
      {showPaymentError && <PaymentErrorAlert error={currentError} onClear={handleClearError} />}

      <Button
        testID={CART_PLACE_ORDER_BUTTON}
        size="lg"
        loading={isButtonLoading}
        disabled={isButtonDisabled}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={isButtonLoading ? 'Processing order' : buttonText}
        accessibilityHint={
          isButtonDisabled
            ? serverOrderError
              ? 'Order cannot be placed due to a pricing error. Please try again'
              : isDeliveryFeesLoading
                ? 'Please wait while delivery fees are calculated'
                : 'Please select a payment method and ensure your cart is not empty'
            : 'Places your order and processes payment'
        }
        accessibilityState={{ disabled: isButtonDisabled, busy: isButtonLoading }}
      >
        {!isButtonLoading && (
          <Button.Icon>
            <IconLocked />
          </Button.Icon>
        )}
        <Button.Text>{buttonText}</Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    borderTopColor: tokens.colors.$black10,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  container: {
    marginHorizontal: 'auto',
    padding: 16,
    width: '100%',
  },
});
